import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Box, Button, IconButton, TextField } from '@mui/material';
import UpAndDownFilterIcon from 'assets/images/iPrise/UpAndDownFilter.svg';
import { useDebounce } from 'hooks';
import { useStore } from 'mobx/StoreContext';
import { observer } from 'mobx-react-lite';
import React, { useCallback, useEffect, useState } from 'react';

import AddAndEditJobV2 from '../../../common/AddAndEditJobV2';
import TeamSelectMenu from '../../../common/components/TeamSelectMenu';
import SortMenu from './SortMenu';

const JobQueueHeader: React.FC = () => {
  const [openAddJob, setOpenAddJob] = useState(false);
  const { managerJobQueueService } = useStore();

  const { selectedTeam, selectedSort, selectedTeamName } =
    managerJobQueueService;

  const [localSearch, setLocalSearch] = useState(
    managerJobQueueService.searchTerm,
  );
  const debouncedSearch = useDebounce(localSearch, 500);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [sortAnchorEl, setSortAnchorEl] = useState<null | HTMLElement>(null);
  const [localPercentage, setLocalPercentage] = useState(
    managerJobQueueService.percentage,
  );
  const debouncedPercentage = useDebounce(localPercentage, 500);

  // Sync debounced search to MobX store
  useEffect(() => {
    managerJobQueueService.setSearchTerm(debouncedSearch);
  }, [debouncedSearch, managerJobQueueService]);

  // Sync debounced percentage to MobX store
  useEffect(() => {
    managerJobQueueService.setPercentage(debouncedPercentage);
  }, [debouncedPercentage, managerJobQueueService]);

  const handleTeamClick = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      setAnchorEl(event.currentTarget);
    },
    [],
  );

  const handleTeamClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleTeamSelect = useCallback(
    (team: string) => {
      managerJobQueueService.setSelectedTeam(team);
      setAnchorEl(null);
    },
    [managerJobQueueService],
  );

  const handleSortClick = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      setSortAnchorEl(event.currentTarget);
    },
    [],
  );

  const handleSortClose = useCallback(() => {
    setSortAnchorEl(null);
  }, []);

  const handleSortSelect = useCallback(
    (sort: string) => {
      managerJobQueueService.setSelectedSort(sort);
      setSortAnchorEl(null);
    },
    [managerJobQueueService],
  );

  const handleSearchChange = useCallback((value: string) => {
    setLocalSearch(value);
  }, []);

  const handlePercentageChange = useCallback((value: string) => {
    setLocalPercentage(value);
  }, []);

  return (
    <Box className="main-tabs-container">
      <Box className="header-left">
        <span className="header-title">Job Queue</span>
      </Box>
      <Box className="header-actions">
        <TextField
          placeholder="Search"
          value={localSearch}
          onChange={(e) => handleSearchChange(e.target.value)}
          className="search-field"
          size="small"
        />
        <Box className="team-select" onClick={handleTeamClick}>
          {selectedTeamName || 'All Teams'}
          <KeyboardArrowDownIcon sx={{ color: '#242424', fontSize: 20 }} />
        </Box>
        <TeamSelectMenu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleTeamClose}
          selectedTeam={selectedTeam}
          onSelect={handleTeamSelect}
        />
        <IconButton className="sort-button" onClick={handleSortClick}>
          <img src={UpAndDownFilterIcon} alt="Sort" />
        </IconButton>
        <SortMenu
          anchorEl={sortAnchorEl}
          open={Boolean(sortAnchorEl)}
          onClose={handleSortClose}
          selectedSort={selectedSort}
          onSelect={handleSortSelect}
        />
        <Box className="percentage-input-container">
          <TextField
            value={localPercentage}
            onChange={(e) => handlePercentageChange(e.target.value)}
            className="percentage-input"
            placeholder="0"
            size="small"
            InputProps={{
              endAdornment: (
                <Box
                  component="span"
                  sx={{ color: '#5E6C84', fontSize: '13px', width: '20px' }}>
                  %
                </Box>
              ),
            }}
          />
        </Box>
        <Button
          variant="contained"
          onClick={() => setOpenAddJob(true)}
          sx={{
            '&:hover': { bgcolor: '#374151', boxShadow: 'none' },
            bgcolor: '#111827',
            borderRadius: '4px',
            boxShadow: 'none',
            color: 'white',
            fontSize: '14px',
            fontWeight: 500,
            height: '32px',
            minWidth: '90px',
            ml: 2,
            textTransform: 'none',
          }}>
          + Add Job
        </Button>
      </Box>
      <AddAndEditJobV2
        open={openAddJob}
        onClose={() => setOpenAddJob(false)}
        scroll="paper"
      />
    </Box>
  );
};

export default observer(JobQueueHeader);
